// React Components
import * as React from 'react';
import { useNavigate } from "react-router-dom";
import { CSVLink } from 'react-csv';
import { useState } from "react";
import axios from "axios";
import moment from 'moment-timezone';

// Material UI Components
import {Box, Grid, Paper } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import DownloadIcon from '@mui/icons-material/Download';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

// Audit Vault Components
import DisplayAndChooseTimezone from './DisplayAndChooseTimezone';

// Audit Vault Utilities
import { getRecordedTime, getUTCOffsetString } from '../../utilities/common-date-utils';
import AUDIT_LOG_RECORD_TYPE from '../../constants/constants-auditlogrecordtypes';
import DisplayFullAuditLogDetail from './DisplayFullAuditLogDetail';
import { encryptKey } from '../../utilities/common-encrypt-util';
import { verifyUserHasTenantRolePermissions } from "../../utilities/common-user-utils";
import { ROLE_CODE_TENANTREPORTREADER } from "../../constants/constants-roles";

/*
Reusable component that displays the exportable audit source data.
Doesn't load any data - just takes the data and displays it.

Updated Aug 26, 2024 - the data provided in reportResults no longer contains the full results, so if a row is clicked we have to load the specific data to be displayed.
This will make the load time much faster for the calling report overall.
This function now has to take in two additional properties when being called, the tenantId and the reportType.

Added Nov 24, 2023 - defaults to display dates in the data table as the browsers' default time zone.  
Allows the User to select a different IANA time zone to display to override the default browser's IANA time zone.

Added Nov 1, 2025 - ability to export all (full dump of all rows sans Privacy Filter for workload in question).

Added May 5, 2026 - To support paging: Added exportAllCsvFilename, if an Export All specific filename was provided by the calling report, use it.
Otherwise fallback to the standard csvFilename value for backwards compatibility with existing reports that do not pass in exportAllCsvFilename.


Parameters:
- columns array (columns to display and style/sizing)
- reportResults array (contains the results)
- csvFilename (exportable csv file name to use)
*/
export default function DisplayExportableDataSource(props) {

    const WEB_API_URL = process.env.REACT_APP_WEB_API_URL;

    // Component Constants        
    const { columns, reportResults, csvFilename, exportAllCsvFilename, tenantId, reportType, user, userAzureTenantId, optionalPayload, optionalHideExportAll=false, optionalHideExpandRow=false } = props;            
    const navigate = useNavigate();    
    
    // TimeZone display related.  Loading and translating the current timezone must be "light weight" in the browser for this page to keep it simple and less intensive on browser rendering.  As such we don't do any complex IANA or Windows timezone conversions on this page (> 400 timezones to translate).  
    const detectedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;  // Browser IANA timezone format.
    const timeZoneBrowserFormatIana = detectedTimeZone && moment.tz.zone(detectedTimeZone)
        ? detectedTimeZone
        : 'Etc/UTC';
    const [timeZoneUTCOffsetFormat, setTimeZoneUTCOffsetFormat] = React.useState(getUTCOffsetString(timeZoneBrowserFormatIana));  // Get the UTC Offset.
    const [selectedTimeZone, setSelectedTimeZone] = React.useState(timeZoneBrowserFormatIana);  // Set default to what the browser reports.
    
    // Data table - settings.
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [sortConfig, setSortConfig] = useState(null);  // Used for sorting column header.
    const [expandedRow, setExpandedRow] = useState(null);  // For Accordion control. 
    const [confirmOpen, setConfirmOpen] = useState(false);  // For the ExportAll confirmation control.
    // For snackbar notification UI element.  
    const [openExportAllStarted, setOpenExportAllStarted] = useState(false); 
    const [exportAllGuid, setExportAllGuid] = useState(""); 
    // Component Functions.


    // Function for when the TimeZone is updated.
    const handleTimeZoneChange = (event) => {
        // Update the state for selectedTimeZone.  This will update the component + re-render automatically in React.
        setSelectedTimeZone(event.target.value);  
        // Update the display UTC format in the text as well.  This will update the component automatically in React.
        setTimeZoneUTCOffsetFormat(getUTCOffsetString(event.target.value));        
    };
    
    // For the optional table to display data source.
    // Function for Sorting column headers.    
    // PW: Updated July 1, 2024: If a row column value is null use empty string to sort instead, before it would not sort null items.
    function getComparator(sortConfig) {
        return (a, b) => {
            if (!sortConfig) {
                return 0;
            }            
            // Convert null values to empty strings for comparison
            const valueA = a[sortConfig.key] != null ? a[sortConfig.key] : '';
            const valueB = b[sortConfig.key] != null ? b[sortConfig.key] : '';            
            if (valueA < valueB) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (valueA > valueB) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        };
    }

    // Function for Sorting column headers.  
    function stableSort(array, comparator) {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
          const order = comparator(a[0], b[0]);
          if (order !== 0) return order;
          return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    }
    // Column header sort function.
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };
    // Change pages in the optional table.
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    // Change rows per page in the optional table.
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    // Accordion row clicked.  Toggles the row open or closed.
    const handleRowClick = (id) => {     
        if(!optionalHideExpandRow)  {
            if (expandedRow === id) {
                setExpandedRow(null);
            }
            else {
                setExpandedRow(id);            
            }        
        }
    };   
    
    // Function to return the Audit Log Record Type - Member Value given the # value we are storing.
    function findAuditLogRecordMemberNameByValue(value) {        
        const record = AUDIT_LOG_RECORD_TYPE.find(record => record.value === value);        
        return record ? record.memberName : "Undefined";
    }

    // Function to call dialog for Export All Insert.
    function handleExportAllInsert() {        
        setConfirmOpen(true);
    }

    async function handleConfirmExportAll() {
        setConfirmOpen(false);
        try {
            //console.log("Export All confirmed by: ", user);            
            //console.log("payload: ", optionalPayload);            
            var payload = {
                // Required fields.
                id: 0,  // Note: The WebAPI function will automatically set the next Id, so this is not required to be filled in.
                companyId: user.companyId,
                tenantId: tenantId,                
                jobCreatedDate: new Date(), // Note: The WebAPI function will automatically use the current server time, so this is not required to be filled in.                
                submittedByUser: await encryptKey(user.userEmail),  // Use the current logged in user (UPN).  We encrypt this one item.                
                jobStatus: 0, // Note: The WebAPI function will automatically set the job status to queued, so this is not required to be filled in.
                reportType: reportType, 
                csvFilename: encodeURIComponent(exportAllCsvFilename || csvFilename),  // If exportAllCsvFilename is not provided, then it will use the csvFilename.
                ...(optionalPayload ? { json: optionalPayload } : { json: "" }), // Only adds the report parameters if truthy, otherwise don't include in payload (some reports have optional parameters like date range, or user name).
                uri: "",                
                userMicrosoftGraphId: await encryptKey(user.microsoftUserId)
            };
            var keyString = JSON.stringify(payload);
            // Call backend API endpoint for export-all
            axios.post(`${WEB_API_URL}Reports/CreateExportAllJob`, null,
                {
                    params: {
                        key: keyString,
                    }
                }
            ).then((response) => {
                // Submits successfully - display dialog showing the GUID waiting for download.  TBD.
                // console.log("Submitted payload: ", payload);
                // console.log("GUID is: ", response);
                setExportAllGuid(response.data);
                setOpenExportAllStarted(true);
            }).catch(e => {
                console.log(e);                
            });        
        
        } catch (error) {
            console.error("Error performing Export All:", error);
        }
    }
    
    // For snackbar close action.
    const handleNotificationClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenExportAllStarted(false);
    };

    // For snackbar notification action.
    const notificationAction = (
        <React.Fragment>
            <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleNotificationClose}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </React.Fragment>
    );

    // Component UI    
    
    // Jan 9, 2025: There are some cases where the browser is returning null even though GetReportData API call is returning Gzipped data.
    // Some browsers are hitting a limit for data, it is browser specific, so check if the reportResults is valid or not before trying to display.
    // This will prevent the javascript error flat() displaying in the browser which is not nice.
    if(Array.isArray(reportResults) == false) {
        return(
            <Alert severity="error">
            <AlertTitle>Browser data limit detected.</AlertTitle>
            Unable to load the source data - your current browser data limit was exceeded.  Please contact support if you have questions.
            <br />As an alternative, we recommend running an Insights Search which allows for a refined result set based on a restricted date range.
            </Alert>
        );
    }    
    // Continue with displaying the results.
    // Aug 22, 2024: Updated, concatenate all report results data arrays to display in a sortable table.  
    // Instead of using reduce we use flatten.  When using reduce and concat it uses alot of memory - creating new arrays and copying elements over.
    // Otherwise reportResults is an array of array's of the datasets.  We need one array with all the data as children.            
    var allReportResults = reportResults.flat();    
    
    // Replace with flatten which improves performance by 50%.
    /*
    var allReportResults = reportResults.reduce((accumulator, reportData) => {
        return accumulator.concat(reportData);
    }, []);
    */    
    // We comment out force sort by Id.  Instead we let the source query be the default sort, please and thanks.
    // var sortedAllReportResults = allReportResults.slice().sort((a, b) => a.id - b.id);  // Sort by Id ascending.
    // allReportResults = sortedAllReportResults;


    // CSVLink appears to automatically escape the CSV column with "" to prevent commas from being a problem.
    // If there are any values containing double quotes we replace with single quote to prevent any issues.
    // Prep CSV headers and data for optional CSV export.  
    // Remap our data table columns to CSV headers.    
    const csvHeaders = columns.map((column) => ({
        label: column.label,
        key: column.id
    }));
    // Prep the CSV data.
    const csvData = allReportResults.map((item) => {
        const tempData = {};        
        columns.forEach((column) => {        
            switch(column.id)  {
                case "creationTime":
                    tempData[column.id] = getRecordedTime(item[column.id], selectedTimeZone);  // Convert time to the Tenant's time.
                    break;
                case "recordType":
                    tempData[column.id] = findAuditLogRecordMemberNameByValue(item[column.id]);  // Convert the # to a text value of MSFT Audit Log Record Types.
                    break;
                case "dispositionItemDeletedDate":
                    tempData[column.id] = getRecordedTime(item[column.id], selectedTimeZone);  // Convert time to the Tenant's time.
                    break;
                default:
                    var theString = item[column.id] ? item[column.id].toString() : "";                
                    tempData[column.id] = theString.replace(/"/g, "'");  // Replace any " with '.                        
                    break;
            }
            /*
            if(column.id != "creationTime") {
                var theString = item[column.id] ? item[column.id].toString() : "";                
                tempData[column.id] = theString.replace(/"/g, "'");  // Replace any " with '.                    
            }
            else {
                tempData[column.id] = getTenantsRecordedTime2(item[column.id]);  // Convert time to the Tenant's time.
            }*/
        });
        return tempData;
    });    


    return (
    <>
    <Box sx={{ mt: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                    Entries Returned: <b>{allReportResults.length}</b>
                </Typography>
            </Grid>

            <Grid
                item
                xs={12}
                sm={6}
                sx={{
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    pr: 1.5, // 👈 adds space from right edge
                }}
            >
                 {allReportResults.length >= 1 ? (
                    <CSVLink
                        data={csvData}
                        headers={csvHeaders}
                        filename={csvFilename}
                        style={{
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            lineHeight: 1,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                cursor: "pointer",
                                color: "primary.main",
                                lineHeight: 1,
                                "&:hover": { textDecoration: "underline" },
                            }}
                        >
                            <DownloadIcon sx={{ fontSize: 18, verticalAlign: "middle" }} />
                            <Box component="span" sx={{ lineHeight: 1 }}>
                                Export
                            </Box>
                        </Box>
                    </CSVLink>
                ) : (
                    <Box
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: "text.disabled",
                            pointerEvents: "none",
                            opacity: 0.5,
                            lineHeight: 1,
                        }}
                    >
                        <DownloadIcon sx={{ fontSize: 18, verticalAlign: "middle" }} />
                        <Box component="span" sx={{ lineHeight: 1 }}>
                            Export
                        </Box>
                    </Box>
                )}

                {verifyUserHasTenantRolePermissions(user, ROLE_CODE_TENANTREPORTREADER) && !optionalHideExportAll ? (
                allReportResults.length >= 1 ? (
                    <Box
                        component="button"
                        type="button"
                        onClick={handleExportAllInsert}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            color: "primary.main",
                            background: "none",
                            border: "none",
                            p: 0,
                            m: 0,
                            lineHeight: 1,
                            font: "inherit",
                            "&:hover": { textDecoration: "underline" },
                        }}
                    >
                        <DownloadIcon sx={{ fontSize: 18, verticalAlign: "middle" }} />
                        <Box component="span" sx={{ lineHeight: 1 }}>
                            Export All
                        </Box>
                    </Box>
                ) : (
                    <Box
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: "text.disabled",
                            pointerEvents: "none",
                            opacity: 0.5,
                            lineHeight: 1,
                        }}
                    >
                        <DownloadIcon sx={{ fontSize: 18, verticalAlign: "middle" }} />
                        <Box component="span" sx={{ lineHeight: 1 }}>
                            Export All
                        </Box>
                    </Box>
                )
            ) : null}

           
            </Grid>
        </Grid>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table
                    sx={{
                        minWidth: 800,
                        '& .MuiTableCell-root': {
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            py: { xs: 0.5, sm: 1 },
                        },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                            fontSize: { xs: '0.75rem', sm: '0.85rem' },
                            fontWeight: 600,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    sx={{
                                        minWidth: column.minWidth,
                                        maxWidth: column.maxWidth,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    }}
                                >
                                    <TableSortLabel
                                        active={sortConfig?.key === column.id}
                                        direction={sortConfig?.key === column.id ? sortConfig.direction : 'asc'}
                                        onClick={() => requestSort(column.id)}
                                    >
                                        {column.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {stableSort(allReportResults, getComparator(sortConfig))
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row) => (
                                <React.Fragment key={row.id}>
                                    <TableRow hover tabIndex={-1}>
                                        {columns.map((column) => {
                                            const value = row[column.id];

                                            switch (column.id) {
                                                case 'creationTime':
                                                    return (
                                                        <TableCell
                                                            key={column.id}
                                                            align={column.align}
                                                            sx={{
                                                                maxWidth: column.maxWidth,
                                                                whiteSpace: 'normal',
                                                                overflowWrap: 'anywhere',
                                                            }}
                                                            onClick={() => handleRowClick(row.id)}
                                                        >
                                                            {value ? getRecordedTime(value, selectedTimeZone) : value}
                                                        </TableCell>
                                                    );

                                                case 'listItemUniqueId':
                                                    return (
                                                        <TableCell
                                                            key={column.id}
                                                            align={column.align}
                                                            sx={{
                                                                maxWidth: column.maxWidth,
                                                                whiteSpace: 'normal',
                                                                overflowWrap: 'anywhere',
                                                            }}
                                                            onClick={() => handleRowClick(row.id)}
                                                        >
                                                            {value &&
                                                            value !== "00000000-0000-0000-0000-000000000000" ? (
                                                                <Link
                                                                    component="button"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        navigate(`/Reports/SharePointItemAuditHistoryReport/${value}`);
                                                                    }}
                                                                    underline="hover"
                                                                >
                                                                    {value}
                                                                </Link>
                                                            ) : (
                                                                value ? value.toString() : ''
                                                            )}
                                                        </TableCell>
                                                    );
                                                
                                                case 'recordType':
                                                    let recordTypeText = "";
                                                    if(value) {
                                                        recordTypeText = findAuditLogRecordMemberNameByValue(value);    
                                                    }
                                                    return(
                                                        <TableCell key={column.id} align={column.align} sx={{ maxWidth: column.maxWidth, whiteSpace: 'normal', wordBreak: 'break-all'}} onClick={() => handleRowClick(row.id)}>
                                                            {value ? recordTypeText : value}
                                                        </TableCell>
                                                    );          
                                                case 'dispositionItemDeletedDate':
                                                    return (
                                                        <TableCell
                                                            key={column.id}
                                                            align={column.align}
                                                            sx={{
                                                                maxWidth: column.maxWidth,
                                                                whiteSpace: 'normal',
                                                                overflowWrap: 'anywhere',
                                                            }}
                                                            onClick={() => handleRowClick(row.id)}
                                                        >
                                                            {value ? getRecordedTime(value, selectedTimeZone) : value}
                                                        </TableCell>
                                                    );                                                                                                        
                                                default:
                                                    return (
                                                        <TableCell
                                                            key={column.id}
                                                            align={column.align}
                                                            sx={{
                                                                maxWidth: column.maxWidth,
                                                                whiteSpace: 'normal',
                                                                overflowWrap: 'anywhere',
                                                            }}
                                                            onClick={() => handleRowClick(row.id)}
                                                        >
                                                            {value ? value.toString() : ''}
                                                        </TableCell>
                                                    );
                                            }
                                        })}
                                    </TableRow>

                                    {expandedRow === row.id && (
                                        <TableRow>
                                            <TableCell colSpan={columns.length}>
                                                <Accordion>
                                                    <AccordionDetails>
                                                        <DisplayFullAuditLogDetail
                                                            tenantId={tenantId}
                                                            reportType={reportType}
                                                            auditLogId={row.microsoftId}
                                                            user={user}
                                                            userAzureTenantId={userAzureTenantId}
                                                        />
                                                    </AccordionDetails>
                                                </Accordion>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={allReportResults.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </Paper>

        <Box sx={{ mt: 3 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item>
                    <Typography variant="body2">
                        * The dates shown in the table above reflect your browser's detected time zone. To display another time zone, select it from the list. Displaying <b>({timeZoneUTCOffsetFormat})</b>
                    </Typography>
                </Grid>
                <Grid item>
                    <DisplayAndChooseTimezone
                        selectedTimeZone={selectedTimeZone}
                        handleTimeZoneChange={handleTimeZoneChange}
                    />
                </Grid>
            </Grid>
        </Box>



    </Box>

    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Export All</DialogTitle>
        <DialogContent>
            <DialogContentText>
                This action will export all rows including the full audit log details/telemetry.
                <br />
                This may take several minutes to be processed. Do you wish to proceed?
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} color="inherit">Cancel</Button>
            <Button onClick={handleConfirmExportAll} color="primary" autoFocus>Proceed</Button>
        </DialogActions>
    </Dialog>

    <Snackbar
        open={openExportAllStarted}
        autoHideDuration={null}
        onClose={handleNotificationClose}
        message={
            <Box sx={{ display: "flex", flexDirection: "column" }}>
                <span>Your Export All job was submitted successfully.</span>
                <Button
                    color="inherit"
                    size="small"
                    onClick={() => window.open(`/Reports/ExportResult/${exportAllGuid}`, "_blank")}
                    sx={{ alignSelf: "flex-start", mt: 0.5 }}
                >
                    View Export Result
                </Button>
                <Button
                    color="inherit"
                    size="small"
                    onClick={() => window.open(`/Reports/MyRecentExports`, "_blank")}
                    sx={{ alignSelf: "flex-start", mt: 0.5 }}
                >
                    My Recent Exports
                </Button>
            </Box>
        }
        action={notificationAction}
    />

    </>
    );
    
}
